import * as React from 'react';
import { connect } from 'react-redux';
import Slider from 'rc-slider';
import { ConfigurationModel, WashBasinModel, ConfigurationDefaultModel, MinMaxModel, WashBasinDefaultModel } from 'franke-trough-dependencies';
import { GlobalPrivateState } from 'src/api';
import { requestAPIActions } from 'loopback-react-request';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UncontrolledTooltip, Button, Tooltip } from 'reactstrap';
import { FormattedMessage } from 'react-intl';
import MESSAGES from './BasinDimensions.messages';
import { DebounceInput } from 'react-debounce-input';
import { WarningsTooltip } from '../../Warnings';
import { isConfigurationEditable } from 'src/redux/utils.redux';
import { faInfo, faCheck } from '@fortawesome/free-solid-svg-icons';
import ConsolesOverlapChoice from '../Assemblies/ConsoleOverlapChoice';

export interface PlateDimensionsProps {
  configuration: GlobalPrivateState['configuration'];
  dispatch: Function;
  warning: GlobalPrivateState['warning'];
  isOpenAccordion?: boolean;
  plateBuilder: GlobalPrivateState['plateBuilder'];
}

export interface PlateDimensionsState {
  slideWidth?: number;
  width?: number;
  widthError: boolean;
  slideDepth?: number;
  depth?: number;
  depthError: boolean;
  plateDimensionsAcceptedNicheTip?: boolean;
  isOpenNicheTip?: boolean;
}

export class PlateDimensionsPartial extends React.Component<PlateDimensionsProps, PlateDimensionsState> {
 
  constructor(props: PlateDimensionsProps) {
    super(props);
    this.state = {
      widthError: false,
      depthError: false,
      isOpenNicheTip: false
    };
    this.handleChange = this.handleChange.bind(this);
    this.handleWidthSlide = this.handleWidthSlide.bind(this);
    this.handleWidthSlideAfterChange = this.handleWidthSlideAfterChange.bind(this);
    this.handleDepthSlide = this.handleDepthSlide.bind(this);
    this.handleDepthSlideAfterChange = this.handleDepthSlideAfterChange.bind(this);
    this.handleClickAcceptNiche = this.handleClickAcceptNiche.bind(this);
  }

  componentDidMount() {
    this.setState({
      depth: this.plateDepth,
      slideDepth: this.plateDepth || (this.plateDepthDefaults && this.plateDepthDefaults.values.min),
      width: this.plateWidth || (this.plateWidthDefaults && this.plateWidthDefaults.values.min),
      slideWidth: this.plateWidth || (this.plateWidthDefaults && this.plateWidthDefaults.values.min),
      isOpenNicheTip: true
    });
  }

  componentDidUpdate(prevProps: PlateDimensionsProps, prevState: PlateDimensionsState) {
    const widthFromStateHasChanged = this.state.width !== prevState.width ? true : false;
    const widthIsDifferentThenFromApi = this.state.width !== this.plateWidth;
    const widthShouldUpdate = !this.state.widthError && widthFromStateHasChanged && widthIsDifferentThenFromApi;

    const depthFromStateHasChanged = this.state.depth !== prevState.depth ? true : false;
    const depthIsDifferentThenFromApi = this.state.depth !== this.plateDepth;
    const depthShouldUpdate = !this.state.depthError && depthFromStateHasChanged && depthIsDifferentThenFromApi;

    if (widthShouldUpdate || depthShouldUpdate) {
      this.dispatchConfiguration(this.state);
    }

    const configuration = this.configuration;
    const prevConfiguration = prevProps.configuration && prevProps.configuration.result;
    if ((configuration && configuration.updated_at) !== (prevConfiguration && prevConfiguration.updated_at)) {
      if (this.state.width !== this.plateWidth) {
        this.setState({
          width: this.plateWidth || (this.plateWidthDefaults && this.plateWidthDefaults.values.min),
        });
      }
      if (this.state.slideWidth !== this.plateWidth) {
        this.setState({
          slideWidth: this.plateWidth || (this.plateWidthDefaults && this.plateWidthDefaults.values.min)
        });
      }
      if (this.state.depth !== this.plateDepth) {
        this.setState({
          depth: this.plateDepth || (this.plateDepthDefaults && this.plateDepthDefaults.values.min),
        });
      }
      if (this.state.slideDepth !== this.plateDepth) {
        this.setState({
          slideDepth: this.plateDepth || (this.plateDepthDefaults && this.plateDepthDefaults.values.min)
        });
      }
    }
  }
  
  public render() {
    if (this.configuration && this.plateWidthDefaults) {

      const { width, depth, widthError, slideWidth, slideDepth } = this.state;

      const depthMin = this.plateDepthDefaults && this.plateDepthDefaults.values.min;
      const depthMax = this.plateDepthDefaults && this.plateDepthDefaults.values.max;
      const depthMarks = {};


      if (this.props.plateBuilder) {
        const recommendationWall = this.props.plateBuilder.calc.recommendationForWallFittings();
        if (depthMin && depthMin <= recommendationWall && depthMax && depthMax >= recommendationWall) {
          depthMarks[recommendationWall] = <div className="rc-slider-mark-labelled">{recommendationWall}<div className="rc-slider-mark-label"><FormattedMessage {...MESSAGES.plateDimensionsDepth470} /></div></div>
        }

        const recommendationStandard = this.props.plateBuilder.calc.recommendationForStandardFittings();
        if (depthMin && depthMin <= recommendationStandard && depthMax && depthMax >= recommendationStandard) {
          depthMarks[recommendationStandard] = <div className="rc-slider-mark-labelled">{recommendationStandard}<div className="rc-slider-mark-label"><FormattedMessage {...MESSAGES.plateDimensionsDepth550} /></div></div>
        }
      }

      return (
        <div className="partial-plate-dimension">
          <div className="form-group position-relative"> 
            {isConfigurationEditable() && (
              <WarningsTooltip itemKey="plateWidth" />
            )}
            <ConsolesOverlapChoice id="dimensionsOverlap" />
            
            {this.props.isOpenAccordion && this.showNicheTooltip && (
              <span className="info-icon" id={'with_niche_tip'}>
                <FontAwesomeIcon icon={faInfo} />
                <Tooltip 
                  autohide={false}
                  isOpen={this.state.isOpenNicheTip} 
                  placement="bottom"
                  target={'with_niche_tip'}
                >
                  <p>
                    <strong>
                      <FormattedMessage {...MESSAGES.depthRequiredHint}/>&nbsp;
                    </strong> 
                    <FormattedMessage {...MESSAGES.withNicheMessage}/>
                  </p>
                  <p className="text-right">
                    <Button 
                      onClick={(ev: React.MouseEvent<HTMLButtonElement>) => this.handleClickAcceptNiche()}
                      className="yes"
                    >
                      <FontAwesomeIcon icon={faCheck} />
                    </Button>
                  </p>
                </Tooltip>
              </span>
            )}
            
            <div className="row">
              <div className="col-12">
                <label className="small mb-2">
                  <FormattedMessage {...MESSAGES.plateDimensionsWidth} />
                  {widthError && (
                    <span className="info-icon" id="UncontrolledTooltipExample">
                      <FontAwesomeIcon icon={faInfo} />
                      <UncontrolledTooltip placement="right" target="UncontrolledTooltipExample">
                        <FormattedMessage  {...MESSAGES.plateDimensionsError} />
                      </UncontrolledTooltip>
                    </span>
                  )}
                </label>
              </div>
            </div>
            <DebounceInput
              className="form-control"
              disabled={this.disabled}
              name="width"
              type="number"
              min={this.plateWidthDefaults.values.min}
              max={this.plateWidthDefaults.values.max}
              onChange={this.handleChange}
              value={width || 0}
            />
          </div>
          <div className="form-group">
            <div className="row">
            <div className="col-3 col-md-2 text-left">
              {this.plateWidthDefaults.values.min}
            </div>
            <div className="col-6 col-md-8 p-0 pt-1">
              <Slider
                min={this.plateWidthDefaults.values.min}
                max={this.plateWidthDefaults.values.max}
                value={slideWidth}
                onChange={this.handleWidthSlide}
                onAfterChange={this.handleWidthSlideAfterChange}
                disabled={this.disabled}
              />
            </div>
            <div className="col-3 col-md-2 text-right">
              {this.plateWidthDefaults.values.max}
            </div>
            </div>
          </div>
          {this.plateDepthDefaults && (
            <>
              <div className="form-group  position-relative">
                {isConfigurationEditable() && (
                  <WarningsTooltip itemKey="plateDepth" />
                )}
                <label className="small mb-2">
                  <FormattedMessage {...MESSAGES.plateDimensionsDepth} />
                </label>
                <DebounceInput
                  className="form-control"
                  disabled={this.disabled}
                  name="depth"
                  type="number"
                  min={depthMin}
                  max={depthMax}
                  onChange={this.handleChange}
                  value={depth || 0}
                />
              </div>
              <div className="form-group pb-3">
                <div className="row mt-4">
                  <div className="col-3 col-md-2 text-left">
                    {depthMin}
                  </div>
                  <div className="col-6 col-md-8 p-0 pt-1">
                    <Slider
                      min={depthMin}
                      max={depthMax}
                      value={slideDepth}
                      onChange={this.handleDepthSlide}
                      onAfterChange={this.handleDepthSlideAfterChange}
                      disabled={this.disabled}
                      marks={depthMarks}
                    />
                  </div>
                  <div className="col-3 col-md-2 text-right">
                    {depthMax}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      );
    }
    return null;
  }

  private get disabled() {
    const { warning } = this.props;

    return !isConfigurationEditable()
      || warning.requireReset.plateWidth.required
      || !this.configuration
      || !this.configuration.washBasinDimension
      || this.showNicheTooltip;
  }

  private get plateWidth(): number | undefined {
    return this.getPlateDimensionsWidth();
  }

  private get plateDepth(): number | undefined {
    return this.getPlateDimensionsDepth();
  }

  private get plateWidthDefaults(): WashBasinDefaultModel['plateWidth'] | undefined {
    return this.defaults && 
      this.defaults.washBasinDimension.plateWidth;
  }

  private get plateDepthDefaults(): WashBasinDefaultModel['plateDepth'] | undefined {
    return this.defaults && 
      this.defaults.washBasinDimension.plateDepth;
  }

  private get configuration(): ConfigurationModel | undefined {
    return this.props.configuration.result;
  }

  private get defaults(): ConfigurationDefaultModel | undefined {
    return this.configuration && this.configuration.defaults;
  }

  private dispatchConfiguration(props: {width?: number, depth?: number, plateDimensionsAcceptedNicheTip?: boolean}): void {
    if (this.disabled && !props.plateDimensionsAcceptedNicheTip) { return; }
    
    const { washBasinDimension } = this.configuration as ConfigurationModel;

    const data: WashBasinModel = washBasinDimension as WashBasinModel;
    if (props.width) { data.plateWidth = props.width; }
    if (props.depth) { data.plateDepth = props.depth; }
    if (props.plateDimensionsAcceptedNicheTip) { data.plateDimensionsAcceptedNicheTip = props.plateDimensionsAcceptedNicheTip; }
    
    this.props.dispatch(requestAPIActions.load(
      'configuration', {
        url: `/Configurations/${this.configuration && this.configuration.id}`,
        options: {
          method: 'PATCH',
          data: {washBasinDimension: data}
        },
      },
      true,
    ));
  }

  private handleClickAcceptNiche() {
    this.dispatchConfiguration({plateDimensionsAcceptedNicheTip: true});
  }

  private getPlateDimensionsDepth(props?: PlateDimensionsProps): number | undefined {
    const { configuration } = props || this.props;
    return configuration && 
      configuration.result &&
      configuration.result.washBasinDimension &&
      configuration.result.washBasinDimension.plateDepth ? configuration.result.washBasinDimension.plateDepth : undefined;
  }

  private getPlateDimensionsWidth(props?: PlateDimensionsProps): number | undefined {
    const { configuration } = props || this.props;
    return configuration && 
      configuration.result &&
      configuration.result.washBasinDimension &&
      configuration.result.washBasinDimension.plateWidth ? configuration.result.washBasinDimension.plateWidth : undefined;
  }

  private handleChange(event: React.SyntheticEvent<HTMLInputElement>): void {
    const target = event.target as HTMLInputElement;
    const value = Math.round(Number(target.value));

    if (target.name === 'width') {
      const isValid = this.plateWidthDefaults ? this.validateMinMax(value, this.plateWidthDefaults.values) : false;

      this.setState({
        slideWidth: isValid && this.state.slideWidth !== value ? value : this.state.slideWidth,
        width: value,
        widthError: !isValid,
      });
    }

    else if (target.name === 'depth') {
      const isValid = this.plateDepthDefaults ? this.validateMinMax(value, this.plateDepthDefaults.values) : false;

      this.setState({
        slideDepth: isValid && this.state.slideDepth !== value ? value : this.state.slideDepth,
        depth: value,
        depthError: !isValid,
      });
    }
  }

  private handleWidthSlide(value: number) {
    value = Math.round(value);
    const isValid = this.plateWidthDefaults ? this.validateMinMax(value, this.plateWidthDefaults.values) : false;
    
    this.setState({
      slideWidth: value,
      widthError: !isValid,
    });
  }

  private handleDepthSlide(value: number) {
    value = Math.round(value);
    const isValid = this.plateDepthDefaults ? this.validateMinMax(value, this.plateDepthDefaults.values) : false;
    
    this.setState({
      slideDepth: value,
      depthError: !isValid,
    });
  }

  
  private handleWidthSlideAfterChange(value: number) {
    value = Math.round(value);
    this.setState({
      width: this.state.width !== value ? value : this.state.width,
    });
  }

  private handleDepthSlideAfterChange(value: number) {
    value = Math.round(value);
    this.setState({
      depth: this.state.depth !== value ? value : this.state.depth,
    });
  }

  private validateMinMax(value: number, values: MinMaxModel): boolean {
    if (value > values.max || value < values.min) {
      return false;
    }
    return true;
  }

  private get showNicheTooltip() {
    return this.configuration && this.configuration.washBasinDimension && typeof this.configuration.washBasinDimension.plateDimensionsAcceptedNicheTip !== 'undefined' && !this.configuration.washBasinDimension.plateDimensionsAcceptedNicheTip || false;
  }
}

export const PlateDimensions = connect((state: GlobalPrivateState) => ({
  plateBuilder: state.plateBuilder,
  configuration: state.configuration,
  warning: state.warning,
}))(PlateDimensionsPartial);

export default PlateDimensions;